import { useEffect, useRef, useState } from "react";
import { Condition, QuestionActionEnum, Survey } from "../models/survey.model";
import {
  Box,
  CircularProgress,
  createTheme,
  Grid,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  Paper,
  Stack,
  styled,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import "../styles/editor.css";
import { Question, QuestionTypeEnum } from "../models/question.model";
import FormDate from "../components/Form/FormDate";
import { useForm } from "react-hook-form";
import FormSelection from "../components/Form/FormSelection";
import FormCheckboxGroup from "../components/Form/FormCheckboxGroup";
import FormRadioGroup from "../components/Form/FormRadioGroup";
import { formatSurveyContent, SurveyEvent } from "../models/survey.event.model";
import { Helmet } from "react-helmet";
import LoadingButton from "../components/LoadingButton";
import { getDeviceId, getDeviceInfo, isDisableFutureQuestion } from "../lib/helper";
import FormInputText from "../components/Form/FormInputText";
import FormUploadFile from "../components/Form/FormUploadFile";
import { FileType } from "../models/form.model";
import CompanyStoreSelection from "./CompanyStoreSelection";
import FormTableRadios from "../components/Form/FormTableRadios";
import { localized, localizedUI, SwitchLanguageButton } from "./SwitchLanguage";
import { useSearchParams } from "react-router-dom";
import type { ImageToolData } from "@nnmax/editorjs-image";
import SurveyEventCompleteView from "./SurveyEventCompleteView";
import moment, { Moment } from "moment";
import SnackMessage from "../components/SnackMessage";
import { CompanyStore } from "../models/company.store.model";
import { storeQuestionLogicId } from "./SurveyForm";
import { getCompanyStoreById } from "../actions/company.store.action";
import { emailPattern } from "../lib/format";

const processLinksInHtml = (html: string): string => {
  return html.replace(/<a(?![^>]*\btarget=)([^>]+)>/gi, '<a target="_blank" rel="noopener noreferrer"$1>');
};

export const edjsParser = (window as any).edjsHTML({
  image: ({ data }: { data: ImageToolData }) => {
    const { url, ...restFile } = data.file;

    const attributes = {
      src: url || "",
      alt: data.caption ? data.caption : "Image",
      ...restFile,
    };

    return `<img ${Object.keys(attributes)
      .filter((key) => {
        const v = attributes[key as keyof typeof attributes];
        return v !== undefined && v !== null;
      })
      .map((k) => `${k}="${attributes[k as keyof typeof attributes]}"`)
      .join(" ")} />`;
  },
  linkTool: ({ data }: any) => {
    return `<a href="${data.link}" target="_blank" rel="noopener noreferrer">${data.meta?.title || data.link}</a>`;
  },
  attaches: ({ data }: { data: { title: string; file: { url: string } } }) => {
    return `<div class="cdx-block">
  <a
    class="cdx-attaches cdx-attaches--with-file"
    href="${data.file?.url || ""}"
    target="_blank"
    rel="noopener noreferrer"
    download
  >
    <div class="cdx-attaches__file-icon">
      <div class="cdx-attaches__file-icon-background">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13.3236 8.43554L9.49533 12.1908C9.13119 12.5505 8.93118 13.043 8.9393 13.5598C8.94741 14.0767 9.163 14.5757 9.53862 14.947C9.91424 15.3182 10.4191 15.5314 10.9422 15.5397C11.4653 15.5479 11.9637 15.3504 12.3279 14.9908L16.1562 11.2355C16.8845 10.5161 17.2845 9.53123 17.2682 8.4975C17.252 7.46376 16.8208 6.46583 16.0696 5.72324C15.3184 4.98066 14.3086 4.55425 13.2624 4.53782C12.2162 4.52138 11.2193 4.91627 10.4911 5.63562L6.66277 9.39093C5.57035 10.4699 4.97032 11.9473 4.99467 13.4979C5.01903 15.0485 5.66578 16.5454 6.79264 17.6592C7.9195 18.7731 9.43417 19.4127 11.0034 19.4374C12.5727 19.462 14.068 18.8697 15.1604 17.7907L18.9887 14.0354"
          ></path>
        </svg>
      </div>
    </div>
    <div class="cdx-attaches__file-info">
      <div
        class="cdx-attaches__title"
        data-placeholder="File title"
      >
        ${data.title}
      </div>
    </div>
    <div class="cdx-attaches__download-button">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="2"
          d="M7 10L11.8586 14.8586C11.9367 14.9367 12.0633 14.9367 12.1414 14.8586L17 10"
        ></path>
      </svg>
    </div>
  </a>
</div>
`
  }
});

const placeholderIndex = 2;

export default function SurveyAppView(props: {
  surveyEventId?: string;
  surveyEvent?: SurveyEvent;
  isPreview?: boolean;
}) {
  const { surveyEventId, surveyEvent: surveyEventProp, isPreview } = props;
  const [surveyEvent, setSurveyEvent] = useState<SurveyEvent | undefined>(surveyEventProp);
  const defaultLanguage = window.navigator?.language?.substring(0, 2).toLowerCase() || "en";
  const [language, setLanguage] = useState<string>(defaultLanguage);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const { survey, company, options } = surveyEvent || {};
  const { autoMove = false, showBack = false } = options || {};
  const [activeIndex, setActiveIndex] = useState(0);
  const isLastQuestion = survey ? activeIndex === survey.questions.length + placeholderIndex - 1 : false;
  const [searchParams] = useSearchParams();
  const searchParamStoreId = searchParams.get("storeId");
  const [showLanguageTip, setShowLanguageTip] = useState(true);
  const methods = useForm<any>({ defaultValues: {} });
  const { control, setError, watch } = methods;
  const [selectedStore, setSelectedStore] = useState<CompanyStore>();
  const startTime = useRef(Date.now()) as React.RefObject<number>;
  const loadingRef = useRef(false);

  const values = watch();

  useEffect(() => {
    const load = async () => {
      if (surveyEventId && !loadingRef.current && !isPreview) {
        try {
          loadingRef.current = true;
          const [customer, store] = await Promise.all([
            getCustomer(),
            searchParamStoreId ? getCompanyStoreById(searchParamStoreId) : Promise.resolve(undefined),
          ]);
          onSelectStore(store);
          const { surveyEvent, surveyEventSubmitId, message } = await window
            .fetch(`${process.env.REACT_APP_API_URL}/public/survey-events/${surveyEventId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                customer,
                surveyEventSubmitId: localStorage.getItem("surveyEventSubmitId"),
              }),
            })
            .then((d) => d.json());
          if (!surveyEvent && message) {
            window.webApp.showMessage(
              "error",
              `Oops, sorry! Load survey failed for some reason: ${message}. Please try again or contact us!`
            );
          }
          setSurveyEvent(surveyEvent);
          if (surveyEventSubmitId) {
            localStorage.setItem("surveyEventSubmitId", surveyEventSubmitId);
          }
        } catch (error: any) {
          window.webApp.showMessage("error", `Sorry some unknown error happened: ${error.message || error}.`);
        }
        loadingRef.current = false;
      }
    };
    load();
  }, [surveyEventId, isPreview]);

  useEffect(() => {
    if (isPreview && surveyEventProp) {
      setSurveyEvent(surveyEventProp);
    }
  }, [isPreview, surveyEventProp])

  const getCustomer = async () => {
    let paramCustomer;
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const params: any = {};
      for (const [key, value] of urlParams) {
        params[key] = value;
      }
      if (params.customer) {
        paramCustomer = JSON.parse(atob(params.customer));
      }
    } catch (error) {
      console.error("try to get customer from url failed", error);
    }

    const customer = {
      uid: paramCustomer?.email || (await getDeviceId()),
      email: paramCustomer?.email,
      name: paramCustomer?.name,
      device: JSON.stringify(getDeviceInfo()),
    };
    return customer;
  };

  const onSelectStore = (store?: CompanyStore) => {
    setSelectedStore(store);
    handleAutoNext(!store)
  };

  const onNext = async () => {
    const i = checkValidCondition(activeIndex === 0 && searchParamStoreId ? activeIndex + 2 : activeIndex + 1, 'next');
    setActiveIndex(i);
  };

  const onBack = () => {
    const i = checkValidCondition(activeIndex - 1, 'back');
    setActiveIndex(i);
  };

  const handleAutoNext = (skip = false) => {
    if (!autoMove || skip || isLastQuestion) return;
    setTimeout(() => {
      onNext();
    }, 300);
  }

  const onComplete = async () => {
    if (!isPreview && surveyEventId) {
      const customer = await getCustomer();
      const storeId = searchParamStoreId || values.storeId;
      delete values.storeId;
      const { success, message } = await window
        .fetch(`${process.env.REACT_APP_API_URL}/public/survey-events/${surveyEventId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer,
            data: JSON.stringify(values),
            storeId,
            surveyEventSubmitId: localStorage.getItem("surveyEventSubmitId"),
            deviceId: await getDeviceId(),
            completionTime: (Date.now() - startTime.current!) / 1000,
            languageCode: language,
          }),
        })
        .then((d) => d.json());
      if (!success && message) {
        window.webApp.showMessage(
          "error",
          `Sorry! Failed to submit the survey: ${message}. Please try again or contact us!`
        );
        throw new Error(message);
      } else {
        window.webApp.showMessage("success", "Your survey has been completed successfully");
      }
    }
  };

  const checkValidCondition = (index: number, type: "next" | "back"): number => {
    if (!survey) {
      console.error("No survey found");
      return 0;
    }
    const question = survey.questions[index - placeholderIndex];
    if (!question) {
      console.warn("No question anymore, should go to finish");
      return index;
    }
    const conditions = question.conditions;
    if (!conditions || conditions.length === 0) {
      return index;
    }

    const getInputValue = (question: Question) => {
      const result =
        question.type !== QuestionTypeEnum.multipleDropdown
          ? values[question.id]
          : Object.keys(values)
              .filter((k) => k.startsWith(question.id))
              .map((k) => `${k.replace(question.id, "")}_rr_${values[k]}`);
      return Array.isArray(result) ? result : [result];
    };

    const meet = conditions.some((a) => {
      if (a.question.id === storeQuestionLogicId) {
        return checkStoreQuestionValidation(a, selectedStore);
      }
      const inputValues = getInputValue(a.question);
      const cons = Array.isArray(a.value) ? a.value : [a.value];
      switch (a.action) {
        case QuestionActionEnum.equal:
          return cons.every((c) => inputValues.includes(c));
        case QuestionActionEnum.notEqual:
          return a.value.length === 0 || cons.some((c) => !inputValues.includes(c));
        case QuestionActionEnum.contain:
          return inputValues.some((val) => cons.includes(val));
        default:
          return false;
      }
    });
    if (!meet) {
      return checkValidCondition(index + (type === 'next' ? 1 : -1), type);
    }
    return index;
  };

  const checkStoreQuestionValidation = (condition: Condition, store: CompanyStore | undefined): boolean => {
    if (!store) {
      return false;
    }
    const selectedStoreId = store.id;
    const { area: selectedArea, region: selectedRegion } = store;
    switch (condition.action) {
      case QuestionActionEnum.equal:
        return (
          selectedArea === condition.value || selectedRegion === condition.value || selectedStoreId === condition.value
        );
      case QuestionActionEnum.notEqual:
        if (typeof condition.value === 'string') {
          return (
            selectedArea !== condition.value && selectedRegion !== condition.value && selectedStoreId !== condition.value
          );
        } else if (Array.isArray(condition.value)) {
          const res =  condition.value.some((v) => selectedArea === v || selectedRegion === v || selectedStoreId === v);
          return !res
        } else {
          return false;
        }
      case QuestionActionEnum.contain:
        return (
          condition.value?.includes(selectedArea) ||
          condition.value?.includes(selectedRegion) ||
          condition.value?.includes(selectedStoreId)
        );
      default:
        return false;
    }
  };

  const checkButtonDisabled = (): [boolean, string] => {
    let errorMessage = "";
    if (activeIndex === 0) {
      return [false, errorMessage];
    }
    if (activeIndex === 1 && values.storeId) {
      return [false, errorMessage];
    }
    const question = survey?.questions[activeIndex - placeholderIndex]?.question;
    if (!question) {
      return [true, errorMessage];
    }
    if (values[`${question.id}`]) {
      return [checkNotAnswered(values[`${question.id}`]), errorMessage];
    }
    if (values[`${question.options[0]?.key}`]) {
      if ([QuestionTypeEnum.datePicker, QuestionTypeEnum.dateTimePicker].includes(question.type)) {
        return [checkDateTime(question, values[`${question.options[0]?.key}`]), errorMessage];
      }
      return [checkNotAnswered(values[`${question.options[0]?.key}`]), errorMessage];
    }

    if ([QuestionTypeEnum.multipleDropdown, QuestionTypeEnum.multipleRowColumnRadio].includes(question.type)) {
      const a = question.options.some((option) => {
        return checkNotAnswered(values[`${question.id}${option.key}`]);
      });
      let hasError = false;
      if (question.id === "question8m0p74zsy6") {
        const sum = question.options.reduce(
          (p, option) => p + parseInt(values[`${question.id}${option.key}`]?.replace(/\D/g, "") || 0),
          0
        );
        hasError = sum > 100;
      }
      if (hasError) {
        errorMessage = "The sum of the selected options must be less than or equal to 100.";
      }
      return [hasError || a, errorMessage];
    }

    return [true, errorMessage];
  };

  const checkNotAnswered = (val: any) => {
    return Array.isArray(val) ? val.length === 0 : !val;
  };

  const checkDateTime = (question: Question, input: string | undefined): boolean => {
    if (!input) return true;
    const inputDate = moment(input);
    if (!inputDate.isValid()) return true;
    const disableFuture = isDisableFutureQuestion(question.options[0].key);
    return disableFuture ? inputDate.isAfter(moment().endOf("day")) : false;
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: options?.primaryColor || "#b860e5",
      },
    },
  });

  useEffect(() => {
    const languages = [...Object.keys(survey?.translations || {}), "en"];
    setAvailableLanguages(languages);
  }, [survey]);

  useEffect(() => {
    if (
      survey?.questions.length &&
      activeIndex >= placeholderIndex &&
      activeIndex < survey.questions.length + placeholderIndex
    ) {
      const languages = [
        ...Object.keys(survey.questions[activeIndex - placeholderIndex]?.question?.translations || {}),
        "en",
      ];
      setAvailableLanguages(languages);
    }
  }, [activeIndex, survey?.questions]);

  useEffect(() => {
    setTimeout(() => {
      setShowLanguageTip(false);
    }, 3000);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Stack
        data-active-index={activeIndex}
        alignItems="center"
        justifyContent="center"
        style={{
          margin: "auto",
          maxWidth: 600,
          minHeight: isPreview ? "100%" : "100vh",
          position: "relative",
          width: "100%",
        }}
        id={"survey-app-view"}
        data-testid="survey-app-view"
      >
        {!isPreview && (
          <Helmet>
            <title>Survey</title>
          </Helmet>
        )}

        {surveyEvent && survey && company ? (
          <Paper sx={{ p: 2, height: "100%", width: "calc(100% - 32px)" }}>
            <Stack
              direction="column"
              sx={{ minHeight: isPreview ? "30.5vw" : "calc(100vh - 64px)" }}
              spacing={4}
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction={"row"} width={"100%"} justifyContent={"space-between"} alignItems={"center"}>
                <LinearProgressWithLabel
                  color="primary"
                  value={(activeIndex / (survey.questions.length + placeholderIndex)) * 100}
                />
                <Tooltip
                  title={"You can change language in here"}
                  arrow={true}
                  open={showLanguageTip}
                  onClick={() => setShowLanguageTip(false)}
                  placement={"bottom-end"}
                >
                  <div>
                    <SwitchLanguageButton setLanguage={setLanguage} availableLanguages={availableLanguages} />
                  </div>
                </Tooltip>
              </Stack>
              {searchParamStoreId && selectedStore?.name && (
                <Typography style={{ marginTop: 0, alignSelf: "flex-start" }} variant="subtitle1">
                  Location: {selectedStore.name}
                </Typography>
              )}
              <div className="width-100p" style={{ flex: 1, display: "flex" }}>
                {activeIndex === 0 && (
                  <div
                    className="html-editor"
                    dangerouslySetInnerHTML={{
                      __html: survey.description
                        ? processLinksInHtml(
                            formatSurveyContent(
                              edjsParser.parse(localized(language, survey, "description", survey.description)).join(""),
                              surveyEvent
                            )
                          )
                        : "",
                    }}
                  />
                )}

                <Stack spacing={4} className="width-100p" display={activeIndex === 1 ? undefined : "none"}>
                  <Typography variant="subtitle1">
                    {localized(
                      language,
                      survey,
                      "storeQuestionText",
                      "Please select from the dropdown menu the location you visited"
                    )}
                  </Typography>
                  <CompanyStoreSelection
                    control={control}
                    language={language}
                    key={language}
                    survey={survey}
                    companyId={company.id}
                    dataTestId={activeIndex === 1 ? "currentquestion-storeId" : ""}
                    onSelectStore={onSelectStore}
                  />
                </Stack>

                {survey.questions.map((v, i) => {
                  return (
                    <div
                      key={`${v.question.id}_${i}`}
                      className="width-100p"
                      style={{
                        display: i + placeholderIndex === activeIndex ? "block" : "none",
                      }}
                    >
                      <Stack direction="column" spacing={2} className="width-100p">
                        <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap" }}>
                          {localized(language, v.question, "content", v.question.content)}
                        </Typography>
                        {(v.question.type === QuestionTypeEnum.dateTimePicker ||
                          v.question.type === QuestionTypeEnum.datePicker) && (
                          <FormDate
                            name={v.question.options[0].key}
                            disableFuture={isDisableFutureQuestion(v.question.options[0].key)}
                            label={formatSurveyContent(
                              localized(language, v.question, v.question.options[0].key, v.question.options[0].label),
                              surveyEvent
                            )}
                            defaultValue={moment()}
                            control={control}
                            setError={setError}
                            type={v.question.type === QuestionTypeEnum.dateTimePicker ? "datetime" : "date"}
                            onAccept={(value: Moment | null) => {
                              handleAutoNext(!value || !value.isValid())
                            }}
                            dataTestId={
                              i + placeholderIndex === activeIndex
                                ? `currentquestion-${v.question.type}-${v.question.options[0].key}`
                                : ""
                            }
                          />
                        )}
                        {v.question.type === QuestionTypeEnum.dropdown && (
                          <FormSelection
                            name={v.question.id}
                            noEmpty
                            options={v.question.options.map((option) => ({
                              label: formatSurveyContent(
                                localized(language, v.question, option.key, option.label),
                                surveyEvent
                              ),
                              value: option.key,
                            }))}
                            control={control}
                            setError={setError}
                            onChange={(value: string) => {
                              handleAutoNext(!value)
                            }}
                            dataTestId={
                              i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                            }
                          />
                        )}
                        {v.question.type === QuestionTypeEnum.multipleDropdown && (
                          <Stack spacing={2} px={2}>
                            {v.question.options.map((option, groupIndex) => (
                              <Grid container key={option.key}>
                                <Grid item container xs={5} alignItems={"center"}>
                                  <div>
                                    {formatSurveyContent(
                                      localized(language, v.question, option.key, option.label),
                                      surveyEvent
                                    )}
                                  </div>
                                </Grid>
                                <Grid item xs={7}>
                                  <FormSelection
                                    name={`${v.question.id}${option.key}`}
                                    control={control}
                                    setError={setError}
                                    noEmpty
                                    options={(option.subOptions || []).map((s) => ({
                                      label: formatSurveyContent(
                                        localized(language, v.question, s.key, s.label),
                                        surveyEvent
                                      ),
                                      value: s.key,
                                    }))}
                                    dataTestId={
                                      i + placeholderIndex === activeIndex
                                        ? `currentquestion-${v.question.type}-group${groupIndex}`
                                        : ""
                                    }
                                  />
                                </Grid>
                              </Grid>
                            ))}
                          </Stack>
                        )}
                        {v.question.type === QuestionTypeEnum.multipleRowColumnRadio && (
                          <FormTableRadios
                            name={v.question.id}
                            control={control}
                            options={v.question.options}
                            localizedText={(key, fallback) => localized(language, v.question, key, fallback)}
                            dataTestId={
                              i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                            }
                          />
                        )}
                        {v.question.type === QuestionTypeEnum.multipleCheckbox && (
                          <FormCheckboxGroup
                            name={v.question.id}
                            groups={[
                              {
                                options: v.question.options.map((o) => ({
                                  label: formatSurveyContent(
                                    localized(language, v.question, o.key, o.label),
                                    surveyEvent
                                  ),
                                  value: o.key,
                                  unique: o.key.toLowerCase().includes("uncheck_all_other"),
                                })),
                              },
                            ]}
                            control={control}
                            setError={setError}
                            dataTestId={
                              i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                            }
                          />
                        )}
                        {(v.question.type === QuestionTypeEnum.singleHorizontalRadio ||
                          v.question.type === QuestionTypeEnum.singleVerticalRadio) && (
                          <FormRadioGroup
                            row={v.question.type === QuestionTypeEnum.singleHorizontalRadio}
                            name={v.question.id}
                            options={v.question.options.map((o) => ({
                              label: formatSurveyContent(localized(language, v.question, o.key, o.label), surveyEvent),
                              value: o.key,
                            }))}
                            control={control}
                            setError={setError}
                            onChange={(value: string) => {
                              handleAutoNext(!value)
                            }}
                            dataTestId={
                              i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                            }
                          />
                        )}
                        {v.question.type === QuestionTypeEnum.textInput && (
                          <FormInputText
                            name={v.question.options[0].key}
                            multiline
                            rows={3}
                            label={formatSurveyContent(
                              localized(language, v.question, v.question.options[0].key, v.question.options[0].label),
                              surveyEvent
                            )}
                            rules={
                              v.question.options[0].key === "text_complain_contact_Iceland"
                                ? {
                                    pattern: {
                                      value: emailPattern,
                                      message: "Please use valid email",
                                    },
                                  }
                                : undefined
                            }
                            control={control}
                            setError={setError}
                            dataTestId={
                              i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                            }
                          />
                        )}
                        {v.question.type === QuestionTypeEnum.imageVideo && (
                          <Stack>
                            <FormUploadFile
                              name={v.question.options[0].key}
                              control={control}
                              setError={setError}
                              multiple
                              max={9}
                              label={localized(
                                language,
                                v.question,
                                v.question.options[0].key,
                                v.question.options[0].label
                              )}
                              prefix={"survey/upload"}
                              type={FileType.file}
                              dataTestId={
                                i + placeholderIndex === activeIndex ? `currentquestion-${v.question.type}` : ""
                              }
                            />
                          </Stack>
                        )}
                      </Stack>
                    </div>
                  );
                })}
                {activeIndex >= survey.questions.length + placeholderIndex && (
                  <SurveyEventCompleteView
                    language={language}
                    survey={survey}
                    eventId={surveyEventId}
                    onComplete={onComplete}
                  />
                )}
              </div>
              <ActionButtons
                activeIndex={activeIndex}
                survey={survey}
                showBack={showBack}
                nextButtonDisabled={checkButtonDisabled()}
                onNext={onNext}
                onBack={onBack}
                language={language}
              />
            </Stack>
          </Paper>
        ) : (
          <CircularProgress />
        )}
      </Stack>
      {!isPreview && <SnackMessage />}
    </ThemeProvider>
  );
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.mode === "light" ? theme.palette.primary.main : theme.palette.primary.dark,
  },
}));

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: "flex", width: "100%", alignItems: "center", py: 2 }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <BorderLinearProgress {...props} variant="determinate" />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary" data-testid={"progress"}>{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

function ActionButtons(props: {
  activeIndex: number;
  survey: Survey;
  nextButtonDisabled: [boolean, string];
  onNext: () => void;
  onBack?: () => void;
  language: string;
  showBack: boolean;
}) {
  const { activeIndex, survey, showBack, nextButtonDisabled, onNext, onBack, language } = props;

  if (activeIndex <= survey.questions.length + placeholderIndex - 1) {
    const shouldShowBack = activeIndex > 0 && showBack;
    const shouldShowBothButtons = shouldShowBack;

    return (
      <Stack gap={1} className="width-100p">
        {nextButtonDisabled[1] && (
          <Typography variant="body2" color="error">
            {nextButtonDisabled[1]}
          </Typography>
        )}
        {shouldShowBothButtons ? (
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" className="width-100p">
            <IconButton
              onClick={onBack}
              data-testid="back-button"
            >
              <ArrowBack />
            </IconButton>
            <LoadingButton
              onClick={onNext}
              disabled={nextButtonDisabled[0]}
              data-testid="Next"
              variant="contained"
              wrapperSx={{ flexBasis: "50%", flexGrow: 0, flexShrink: 1 }}
              sx={{ width: "100%" }}
            >
              {localizedUI(language, "next")}
            </LoadingButton>
          </Stack>
        ) : (
          <LoadingButton
            className="width-100p"
            variant="contained"
            disabled={nextButtonDisabled[0]}
            onClick={onNext}
            data-testid="Next"
          >
            {activeIndex > 0 ? localizedUI(language, "next") : localizedUI(language, "start")}
          </LoadingButton>
        )}
      </Stack>
    );
  }

  return null;
}
